import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'components/DashboardNavbar';
import Footer from 'examples/Footer';
import { useDashboardTheme, ACCENT_COLOR, cardSx } from './utils';

import {
  getRoomLiveState,
  getRoomEngineInfo,
  getRoomSummary,
  getRoomNetMetrics,
  getRoomBlocks,
  getRoomBids,
  getRoomBossCycles,
  getRoomAirdropHistory,
  getNicehashBalance,
  listSessionSnapshots
} from 'services/nh-room-dashboard';

import KPICards from './components/KPICards';
import RoomHeader from './components/RoomHeader';
import LiveLeaderboard from './components/LiveLeaderboard';
import PlayerBreakdown from './components/PlayerBreakdown';
import RecentBidsFeed from './components/RecentBidsFeed';
import BossCyclePanel from './components/BossCyclePanel';
import MiningOverview from './components/MiningOverview';
import BlockMiningHistory from './components/BlockMiningHistory';
import AirdropMeterPanel from './components/AirdropMeterPanel';
import AirdropDistributionHistory from './components/AirdropDistributionHistory';
import NetPLBreakdown from './components/NetPLBreakdown';
import DashboardCharts from './components/DashboardCharts';
import RewardDistributionPie from './components/RewardDistributionPie';
import LastWinsTicker from './components/LastWinsTicker';

const POLL_INTERVALS = { 5: '5s', 10: '10s', 30: '30s' };

function NHRoomDashboard() {
  const { roomId } = useParams();
  const navigate = useNavigate();

  const [liveState, setLiveState] = useState(null);
  const [engineInfo, setEngineInfo] = useState(null);
  const [summary, setSummary] = useState(null);
  const [netMetrics, setNetMetrics] = useState(null);
  const [blocks, setBlocks] = useState({ data: [], total: 0 });
  const [bids, setBids] = useState({ data: [], total: 0 });
  const [bossCycles, setBossCycles] = useState({ data: [], total: 0 });
  const [airdropHistory, setAirdropHistory] = useState({ data: [], total: 0 });
  const [nicehashBalance, setNicehashBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [pollInterval, setPollInterval] = useState(5);
  const [chartBucket, setChartBucket] = useState('minute');

  // roomGone: room no longer exists in Redis (404), distinct from network errors
  const [roomGone, setRoomGone] = useState(false);

  const fetchLiveData = useCallback(async () => {
    if (!roomId) return;
    try {
      const [lsResult, ei] = await Promise.all([
        getRoomLiveState(roomId),
        getRoomEngineInfo(roomId)
      ]);
      const stable = (prev, next) => JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
      if (lsResult.data) {
        setLiveState((p) => stable(p, lsResult.data));
      } else if (lsResult.notFound) {
        // Room genuinely gone from Redis (404), not a network error
        setRoomGone(true);
      }
      // On network error (data=null, notFound=false): keep existing liveState, ignore
      if (ei != null) setEngineInfo((p) => stable(p, ei));
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Live data fetch error:', err);
    }
  }, [roomId]);

  const fetchSlowData = useCallback(async () => {
    if (!roomId) return;
    try {
      const [sm, nm, bl, bd, bc, ah, nhBal] = await Promise.all([
        getRoomSummary(roomId),
        getRoomNetMetrics(roomId, chartBucket),
        getRoomBlocks(roomId, 50, 0),
        getRoomBids(roomId, 50, 0),
        getRoomBossCycles(roomId, 20, 0),
        getRoomAirdropHistory(roomId, 20, 0),
        getNicehashBalance()
      ]);
      // Only update state when data actually changed — prevents Chart.js
      // destroy/recreate flicker on every poll when data is identical.
      const stable = (prev, next) => JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
      if (sm) setSummary((p) => stable(p, sm));
      if (nm) setNetMetrics((p) => stable(p, nm));
      if (bl) setBlocks((p) => stable(p, bl));
      if (bd) setBids((p) => stable(p, bd));
      if (bc) setBossCycles((p) => stable(p, bc));
      if (ah) setAirdropHistory((p) => stable(p, ah));
      if (nhBal) setNicehashBalance((p) => stable(p, nhBal));
    } catch (err) {
      console.error('Slow data fetch error:', err);
    }
  }, [roomId, chartBucket]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchLiveData(), fetchSlowData()]);
    setLoading(false);
  }, [fetchLiveData, fetchSlowData]);

  // Fetch data on mount and when chartBucket changes (fetchAll depends on fetchSlowData which depends on chartBucket)
  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // ── Redirect to session detail when event ends ─────────────────────
  const redirectAttempted = useRef(false);
  const redirectTimerRef = useRef(null);

  // Cleanup redirect timer on unmount
  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
    };
  }, []);

  // Attempt to find and navigate to the session snapshot.
  // Retries up to maxRetries times (snapshot may not be written yet).
  const attemptRedirect = useCallback((retryCount = 0, maxRetries = 3) => {
    if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
    listSessionSnapshots({ roomId, limit: 1, offset: 0 }).then((res) => {
      if (res?.data?.[0]?.id) {
        navigate(`/nh-session/${res.data[0].id}`, { replace: true });
      } else if (retryCount < maxRetries) {
        // Snapshot not written yet — retry after delay
        redirectTimerRef.current = setTimeout(() => {
          attemptRedirect(retryCount + 1, maxRetries);
        }, 3000);
      }
      // After max retries, give up silently — page stays with auto-refresh off
    });
  }, [roomId, navigate]);

  // Trigger redirect when session ends via either path:
  // (a) eventStatus becomes 'ended'/'disabled' in liveState
  // (b) room disappears from Redis (roomGone=true after successful prior fetch)
  const prevEventStatus = useRef(null);
  useEffect(() => {
    if (redirectAttempted.current) return;

    let shouldRedirect = false;

    // Path A: eventStatus transitioned to ended/disabled
    if (liveState?.eventStatus) {
      const currentStatus = liveState.eventStatus;
      const isEnded = currentStatus === 'ended' || currentStatus === 'disabled';

      if (isEnded) {
        shouldRedirect = true;
      }
      prevEventStatus.current = currentStatus;
    }

    // Path B: room gone from Redis (404) — only if we had data before (not initial load failure)
    if (roomGone && (liveState || prevEventStatus.current)) {
      shouldRedirect = true;
    }

    if (shouldRedirect) {
      redirectAttempted.current = true;
      setAutoRefresh(false);
      // If room just ended, give backend 2s to write the snapshot
      const delay = roomGone ? 1000 : 2000;
      redirectTimerRef.current = setTimeout(() => {
        attemptRedirect(0, 3);
      }, delay);
    }
  }, [liveState, roomGone, attemptRedirect]);

  // On initial load: if first fetch found no liveState AND room is gone, disable polling
  // (No redirect — we never had liveState, so this is likely a stale/bookmarked URL)
  useEffect(() => {
    if (!loading && !liveState && roomGone && !redirectAttempted.current) {
      redirectAttempted.current = true;
      setAutoRefresh(false);
      // Still attempt redirect — room existed before, snapshot likely exists
      attemptRedirect(0, 3);
    }
  }, [loading, liveState, roomGone, attemptRedirect]);

  // Polling
  useEffect(() => {
    if (!autoRefresh || !roomId) return;

    const liveTimer = setInterval(fetchLiveData, pollInterval * 1000);
    const slowTimer = setInterval(fetchSlowData, 15000);

    return () => {
      clearInterval(liveTimer);
      clearInterval(slowTimer);
    };
  }, [autoRefresh, pollInterval, roomId, fetchLiveData, fetchSlowData]);

  if (!roomId) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3}>
          <MDTypography variant='h5'>No room ID provided</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  const t = useDashboardTheme();

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={{ xs: 1, md: 3 }}>
        {/* Header */}
        <RoomHeader
          roomId={roomId}
          liveState={liveState}
          summary={summary}
          engineInfo={engineInfo}
          lastUpdated={lastUpdated}
          loading={loading}
        />

        {/* Controls */}
        <MDBox mb={3}>
          <MDBox
            sx={{
              p: 2,
              borderRadius: '12px',
              background: t.isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
              border: `1px solid ${t.divider}`
            }}
          >
            <MDBox display='flex' alignItems='center' gap={0.75} mb={1.5}>
              <Icon sx={{ fontSize: '0.9rem !important', color: t.isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)' }}>tune</Icon>
              <MDTypography variant='button' fontWeight='bold' sx={{ fontSize: '0.68rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: t.isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' }}>
                Controls
              </MDTypography>
            </MDBox>
            <MDBox display='flex' alignItems='center' gap={2} flexWrap='wrap'>
              <FormControlLabel
                control={
                  <Switch
                    checked={autoRefresh}
                    onChange={(e) => setAutoRefresh(e.target.checked)}
                    color='info'
                  />
                }
                label={
                  <MDTypography variant='button' color='text' sx={{ fontSize: '0.78rem' }}>
                    Auto-refresh
                  </MDTypography>
                }
              />
              <Select
                size='small'
                value={pollInterval}
                onChange={(e) => setPollInterval(Number(e.target.value))}
                sx={{ minWidth: 80 }}
              >
                {Object.entries(POLL_INTERVALS).map(([val, label]) => (
                  <MenuItem key={val} value={Number(val)}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
              <Select
                size='small'
                value={chartBucket}
                onChange={(e) => setChartBucket(e.target.value)}
                sx={{ minWidth: 100 }}
              >
                <MenuItem value='minute'>Minute</MenuItem>
                <MenuItem value='hour'>Hourly</MenuItem>
                <MenuItem value='day'>Daily</MenuItem>
              </Select>
              <MDButton variant='outlined' color='info' size='small' onClick={fetchAll} sx={{ borderRadius: '10px', fontWeight: 600, fontSize: '0.72rem' }}>
                <Icon sx={{ mr: 0.5, fontSize: '1rem !important' }}>refresh</Icon>
                Refresh Now
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>

        {/* Last Wins Ticker */}
        {liveState?.lastWins?.length > 0 && <LastWinsTicker lastWins={liveState.lastWins} />}

        {/* Row 1: KPI Cards */}
        <KPICards summary={summary} engineInfo={engineInfo} liveState={liveState} netMetrics={netMetrics} nicehashBalance={nicehashBalance} loading={loading} />

        {/* Row 2: Two-Column Layout */}
        <Grid container spacing={2} mb={3}>
          {/* Left Column: Players & Bidding (7/12) */}
          <Grid item xs={12} lg={7}>
            <MDBox display='flex' flexDirection='column' gap={2}>
              {liveState?.leaderboard?.length > 0 && (
                <LiveLeaderboard liveState={liveState} loading={loading} />
              )}
              {liveState?.players?.length > 0 && (
                <PlayerBreakdown liveState={liveState} loading={loading} />
              )}
              <RecentBidsFeed bids={bids} loading={loading} />
              <BossCyclePanel liveState={liveState} bossCycles={bossCycles} loading={loading} />
            </MDBox>
          </Grid>

          {/* Right Column: Mining & Airdrop (5/12) */}
          <Grid item xs={12} lg={5}>
            <MDBox display='flex' flexDirection='column' gap={2}>
              {engineInfo && (
                <MiningOverview engineInfo={engineInfo} liveState={liveState} />
              )}
              <BlockMiningHistory blocks={blocks} loading={loading} roomId={roomId} knownCoins={liveState?.rewardsBoards?.map((rb) => rb.currency)} />
              {liveState?.airdropMeter && (
                <AirdropMeterPanel liveState={liveState} loading={loading} />
              )}
              {(airdropHistory?.total > 0 || airdropHistory?.data?.length > 0) && (
                <AirdropDistributionHistory airdropHistory={airdropHistory} loading={loading} />
              )}
            </MDBox>
          </Grid>
        </Grid>

        {/* Row 3: Financial Summary & Charts */}
        <NetPLBreakdown netMetrics={netMetrics} summary={summary} engineInfo={engineInfo} liveState={liveState} nicehashBalance={nicehashBalance} loading={loading} />
        {liveState?.rewardPercentages && (
          <RewardDistributionPie liveState={liveState} loading={loading} />
        )}
        <DashboardCharts netMetrics={netMetrics} loading={loading} />
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default NHRoomDashboard;
